import { getCurrentPosition } from './geolocation';

export type PermissionStatus =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unsupported'
  | 'unknown';

export type LocationResult =
  | { ok: true; lat: number; lng: number }
  | {
      ok: false;
      reason: 'denied' | 'unsupported' | 'unavailable' | 'timeout' | 'cancelled';
    };

// Cached permission state. `primePermissionState()` populates it once at app
// load; `requestLocation()` then reads it synchronously so calls from a
// click handler don't await anything before `getCurrentPosition()` —
// Safari's user-activation window is sensitive to intermediate awaits and
// will silently downgrade a real prompt to a denial.
let cachedPermissionState: PermissionStatus = 'unknown';
let primePromise: Promise<PermissionStatus> | null = null;

export function getCachedPermissionState(): PermissionStatus {
  return cachedPermissionState;
}

export async function primePermissionState(): Promise<PermissionStatus> {
  if (primePromise) return primePromise;
  primePromise = (async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      cachedPermissionState = 'unsupported';
      return cachedPermissionState;
    }
    if (!navigator.permissions) {
      // Older browsers without the Permissions API — assume prompt and
      // let getCurrentPosition surface the actual outcome.
      cachedPermissionState = 'prompt';
      return cachedPermissionState;
    }
    try {
      const status = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });
      cachedPermissionState = status.state;
      // Live updates if the user toggles permission in browser settings
      // while the app is open.
      status.onchange = () => {
        cachedPermissionState = status.state;
      };
    } catch {
      cachedPermissionState = 'prompt';
    }
    return cachedPermissionState;
  })();
  return primePromise;
}

// Shared primer-modal state. <LocationPrimer /> reads this and renders the
// dialog; `requestLocation()` writes it when permission is in the prompt
// state and `resolvePrimer()` settles the pending request when the user
// makes their choice.
export const primerState = $state<{ open: boolean; feature: string | null }>({
  open: false,
  feature: null,
});

let primerResolver: ((allow: boolean) => void) | null = null;

function showPrimer(feature: string): Promise<boolean> {
  return new Promise((resolve) => {
    primerResolver = resolve;
    primerState.feature = feature;
    primerState.open = true;
  });
}

export function resolvePrimer(allow: boolean): void {
  const r = primerResolver;
  primerResolver = null;
  primerState.open = false;
  primerState.feature = null;
  if (r) r(allow);
}

/**
 * Request the user's location with a context-appropriate UX.
 *
 * - `granted`: direct `getCurrentPosition()` with no intervening awaits.
 * - `denied` / `unsupported`: return early with a reason; caller surfaces
 *   the appropriate help text.
 * - `prompt` / `unknown`: open the shared primer modal first; the native
 *   browser prompt fires only after the user clicks Allow (a fresh
 *   user-activation gesture).
 */
export async function requestLocation(
  opts: { feature?: string } = {},
): Promise<LocationResult> {
  // Fast path — granted reads from the synchronous cache.
  if (cachedPermissionState === 'granted') return fetchAndSyncState();
  if (cachedPermissionState === 'denied') return { ok: false, reason: 'denied' };
  if (cachedPermissionState === 'unsupported') {
    return { ok: false, reason: 'unsupported' };
  }

  // Unknown — prime first (priming should have happened at app load,
  // but the request may arrive before the prime promise resolves).
  if (cachedPermissionState === 'unknown') {
    const state = await primePermissionState();
    if (state === 'granted') return fetchAndSyncState();
    if (state === 'denied') return { ok: false, reason: 'denied' };
    if (state === 'unsupported') return { ok: false, reason: 'unsupported' };
  }

  // 'prompt' — open the shared primer modal.
  const allowed = await showPrimer(opts.feature ?? 'this feature');
  if (!allowed) return { ok: false, reason: 'cancelled' };
  return fetchAndSyncState();
}

/**
 * Wrap `getCurrentPosition()` so the permission cache reflects what
 * actually happened. Crucial on Safari, where
 * `navigator.permissions.query({name: 'geolocation'})` is unreliable —
 * the cache stays at 'prompt' forever otherwise, and the primer modal
 * re-opens on every subsequent call even after the user has granted.
 *
 * Only sync on definitive outcomes (granted, denied). 'unavailable' or
 * 'timeout' don't tell us about the permission, just the signal — leave
 * the cache alone.
 */
async function fetchAndSyncState(): Promise<LocationResult> {
  const result = await getCurrentPosition();
  if (result.ok) {
    cachedPermissionState = 'granted';
  } else if (result.reason === 'denied') {
    cachedPermissionState = 'denied';
  }
  return result;
}
